'use client';

import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  OutputField,
  StyledDatePicker,
  StyledTextField,
} from './components';

type PayslipFormValues = {
  fromDate: string;
  toDate: string;
  netAmount: string;
};

const formatCurrency = (value: number) =>
  value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

const PayslipIncomeEstimator = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const methods = useForm<PayslipFormValues>({
    defaultValues: {
      fromDate: '',
      toDate: '',
      netAmount: '',
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const parsedNetAmount = parseFloat(values.netAmount) || 0;

  const results = useMemo(() => {
    if (!values.fromDate || !values.toDate || parsedNetAmount <= 0) return null;

    const start = dayjs(values.fromDate);
    const end = dayjs(values.toDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return null;

    const days = end.diff(start, 'day') + 1;
    const months = days / 30.4375;
    const years = days / 365.25;

    return {
      days,
      monthlyIncome: parsedNetAmount / months,
      annualIncome: parsedNetAmount / years,
    };
  }, [values.fromDate, values.toDate, parsedNetAmount]);

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="PayslipIncomeEstimator"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Enter payslip details'}>
          <Controller
            name="fromDate"
            control={control}
            render={({ field }) => (
              <StyledDatePicker
                label="From Date"
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
          <Controller
            name="toDate"
            control={control}
            render={({ field }) => (
              <StyledDatePicker
                label="To Date"
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
          <Controller
            name="netAmount"
            control={control}
            render={({ field }) => (
              <StyledTextField
                label="Net Amount Earned"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
                type="number"
                inputProps={{ min: 0, step: 1 }}
              />
            )}
          />
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Estimated Income Breakdown'}>
          {results ? (
            <>
              <OutputField label="Days in Period" value={results.days} />
              <OutputField
                label="Monthly Equivalent"
                value={formatCurrency(results.monthlyIncome)}
              />
              <OutputField label="Annual Equivalent" value={formatCurrency(results.annualIncome)} />
            </>
          ) : (
            <OutputField
              label="Enter valid dates and income to see results"
              value=""
              sx={{
                textAlign: 'center',
                dt: {
                  mt: 1.1,
                  mb: 1,
                },
              }}
            />
          )}
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default PayslipIncomeEstimator;
